/**
 * Orquestrador do Kanban: contextos, filtros, modais, deep-link e API da View.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Lead, Page, Contact } from '../../../types';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { useAppContext } from '../../../contexts/AppContext';
import { notify } from '../../../utils/toast';
import { createModuleLogger } from '../../../utils/logger';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useUsers } from '../../../contexts/users/UsersContext';
import { useTasks } from '../../../contexts/tasks/TasksContext';
import { useKanbanFilters } from './useKanbanFilters';
import { useKanbanChat } from './useKanbanChat';
import { useKanbanModals } from './useKanbanModals';
import { submitNewLeadFromModalForm } from '../utils/leadFormHelpers';
import { LeadQueries } from '../../../infrastructure/repositories/queries/LeadQueries';
import { mapDomainLeadToFrontend, noopLeadHistoryCache } from '../../../contexts/leads/utils/leadMappers';
import { supabase } from '../../../lib/supabase';

const log = createModuleLogger('[Kanban]');
const VIEW_MODE_KEY = 'kanban_view_mode';
const OPEN_LEAD_LS_KEY = 'KANBAN_OPEN_LEAD_ID';
const OPEN_LEAD_SESSION_KEY = 'kanban_open_lead_id';

function readViewMode(): 'kanban' | 'list' {
    if (typeof window === 'undefined') return 'kanban';
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return saved === 'kanban' || saved === 'list' ? saved : 'kanban';
}

/** Lê e limpa IDs de deep-link (localStorage + sessionStorage). */
function peekOpenLeadId(): { leadId: string | null; clear: () => void } {
    const fromLocal = localStorage.getItem(OPEN_LEAD_LS_KEY);
    const fromSession = sessionStorage.getItem(OPEN_LEAD_SESSION_KEY);
    return {
        leadId: fromLocal || fromSession,
        clear: () => {
            if (fromLocal) localStorage.removeItem(OPEN_LEAD_LS_KEY);
            if (fromSession) sessionStorage.removeItem(OPEN_LEAD_SESSION_KEY);
        },
    };
}

export const useKanban = () => {
    const routePrevRef = useRef<Page | null>(null);
    const deepLinkFetchRef = useRef<string | null>(null);

    const {
        funnelStages, leads, funnels, selectedFunnelId, setSelectedFunnelId,
        saveLead, deleteLead, refreshLeads,
    } = useLeads();
    const { tasks } = useTasks();
    const { setCurrentPage, currentPage } = useAppContext();
    const { contacts, createContact, refreshContacts } = useCrmData();
    const { crmUsers } = useUsers();

    const [createdContactForDeal, setCreatedContactForDeal] = useState<Contact | null>(null);
    const [viewMode, setViewMode] = useState<'kanban' | 'list'>(readViewMode);

    // Auto-select: 1 funil → sempre; vários → mantém válido ou cai no primeiro
    useEffect(() => {
        if (funnels.length === 0) return;
        const firstId = funnels[0].id;
        if (funnels.length === 1) {
            if (selectedFunnelId !== firstId) setSelectedFunnelId(firstId);
            return;
        }
        if (!selectedFunnelId || !funnels.some((f) => f.id === selectedFunnelId)) {
            setSelectedFunnelId(firstId);
        }
    }, [selectedFunnelId, funnels, setSelectedFunnelId]);

    // Voltar ao Kanban: realinha lista com o servidor
    useEffect(() => {
        const previous = routePrevRef.current;
        routePrevRef.current = currentPage;
        if (currentPage === 'kanban' && previous !== null && previous !== 'kanban') {
            void refreshLeads(selectedFunnelId ?? undefined);
        }
    }, [currentPage, selectedFunnelId, refreshLeads]);

    const {
        searchTerm, setSearchTerm, filters, setFilters, filteredLeads, leadsByStage,
    } = useKanbanFilters(leads, funnelStages, tasks);

    const { handleChatClick } = useKanbanChat(contacts, setCurrentPage);

    const {
        isFormModalOpen, preselectedStageId, selectedLead,
        isDeleteModalOpen, leadToDelete, isContactModalOpen,
        handleOpenCreateModal, closeFormModal, handleOpenEditPanel,
        handleClosePanel, handleOpenDeleteModal, closeDeleteModal,
        handleOpenContactModal, handleCloseContactModal,
    } = useKanbanModals();

    useEffect(() => {
        localStorage.setItem(VIEW_MODE_KEY, viewMode);
    }, [viewMode]);

    const handleSaveLeadAndClosePanel = useCallback(async (lead: Lead) => {
        if (await saveLead(lead)) handleClosePanel();
    }, [saveLead, handleClosePanel]);

    const confirmDeleteLead = useCallback(() => {
        if (!leadToDelete) return;
        deleteLead(leadToDelete.id);
        closeDeleteModal();
    }, [leadToDelete, deleteLead, closeDeleteModal]);

    const handleSaveLeadFromModal = useCallback(async (formData: any): Promise<boolean> => {
        log.log('Iniciando salvamento de lead', formData);
        const contactsForValidation =
            createdContactForDeal && !contacts.some((c) => c.id === createdContactForDeal.id)
                ? [...contacts, createdContactForDeal]
                : contacts;

        const ok = await submitNewLeadFromModalForm(
            formData, contactsForValidation, funnelStages, preselectedStageId, saveLead,
        );
        if (!ok) return false;
        notify.success('Negociação criada com sucesso');
        closeFormModal();
        return true;
    }, [contacts, createdContactForDeal, preselectedStageId, funnelStages, saveLead, closeFormModal]);

    const handleSaveContactFromModal = useCallback(async (contactData: Omit<Contact, 'id'>) => {
        try {
            const saved = await createContact(contactData);
            await refreshContacts();
            setCreatedContactForDeal(saved);
            handleCloseContactModal();
            notify.success('Contato criado com sucesso!');
        } catch (error) {
            log.error('Erro ao salvar contato:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao salvar contato.');
            throw error;
        }
    }, [createContact, refreshContacts, handleCloseContactModal]);

    const clearCreatedContactForDeal = useCallback(() => setCreatedContactForDeal(null), []);
    const handleCreateFunnel = useCallback(() => {
        setSelectedFunnelId(null);
        setCurrentPage('create-funnel');
    }, [setCurrentPage, setSelectedFunnelId]);
    const handleEditFunnel = useCallback(() => setCurrentPage('edit-funnel'), [setCurrentPage]);
    const handleRefresh = useCallback(() => window.location.reload(), []);

    // Deep-link / restore lead aberto (localStorage ou sessionStorage)
    useEffect(() => {
        try {
            const { leadId: leadIdToOpen, clear } = peekOpenLeadId();
            if (!leadIdToOpen || selectedLead) return;

            const fromList = leads.find((l) => l.id === leadIdToOpen);
            if (fromList) {
                handleOpenEditPanel(fromList);
                clear();
                deepLinkFetchRef.current = null;
                return;
            }

            if (deepLinkFetchRef.current === leadIdToOpen) return;
            deepLinkFetchRef.current = leadIdToOpen;

            let cancelled = false;
            void (async () => {
                try {
                    const domainLead = await LeadQueries.findById(leadIdToOpen);
                    if (cancelled || !domainLead) {
                        deepLinkFetchRef.current = null;
                        return;
                    }
                    const mapped = mapDomainLeadToFrontend(domainLead, noopLeadHistoryCache);

                    let funnelForRefresh: string | null | undefined = selectedFunnelId ?? undefined;
                    const stageInCurrent =
                        !!mapped.stage && funnelStages.some((s) => s.id === mapped.stage);

                    if (!stageInCurrent && mapped.stage) {
                        const { data: stageRow } = await supabase
                            .from('funnel_stages')
                            .select('funnel_id')
                            .eq('id', mapped.stage)
                            .maybeSingle();
                        if (stageRow?.funnel_id) {
                            funnelForRefresh = stageRow.funnel_id;
                            setSelectedFunnelId(stageRow.funnel_id);
                        }
                    }

                    await refreshLeads(
                        funnelForRefresh !== undefined ? funnelForRefresh : selectedFunnelId ?? null,
                    );
                    handleOpenEditPanel(mapped);
                    clear();
                } catch (e) {
                    log.warn('Deep link: could not load lead by id', e);
                    deepLinkFetchRef.current = null;
                }
            })();

            return () => {
                cancelled = true;
                deepLinkFetchRef.current = null;
            };
        } catch (e) {
            log.error('Error auto-opening lead:', e);
        }
    }, [
        leads, selectedLead, handleOpenEditPanel, refreshLeads,
        selectedFunnelId, setSelectedFunnelId, funnelStages,
    ]);

    return useMemo(() => ({
        viewMode, setViewMode, searchTerm, setSearchTerm, filters, setFilters, selectedLead,
        isOpportunitySidebarOpen: isFormModalOpen,
        isDeleteModalOpen, isContactModalOpen, leadToDelete, selectedFunnelId,
        filteredLeads, leadsByStage, contacts, funnels, funnelStages, users: crmUsers,
        handleCardClick: handleOpenEditPanel,
        handleClosePanel, handleSaveLeadAndClosePanel, handleOpenCreateModal,
        handleSaveLeadFromModal, closeFormModal, handleOpenDeleteModal, confirmDeleteLead,
        closeDeleteModal, setSelectedFunnelId, handleCreateFunnel, handleEditFunnel,
        handleOpenContactModal, handleCloseContactModal, handleSaveContactFromModal,
        createdContactForDeal, clearCreatedContactForDeal, handleChatClick, handleRefresh,
    }), [
        viewMode, searchTerm, filters, filteredLeads, leadsByStage, selectedLead,
        isFormModalOpen, isDeleteModalOpen, isContactModalOpen, leadToDelete,
        selectedFunnelId, contacts, funnels, funnelStages, crmUsers,
        handleOpenEditPanel, handleClosePanel, handleSaveLeadAndClosePanel,
        handleOpenCreateModal, handleSaveLeadFromModal, closeFormModal,
        handleOpenDeleteModal, confirmDeleteLead, closeDeleteModal,
        setSelectedFunnelId, handleCreateFunnel, handleEditFunnel,
        handleOpenContactModal, handleCloseContactModal, handleSaveContactFromModal,
        createdContactForDeal, clearCreatedContactForDeal, handleChatClick, handleRefresh,
    ]);
};
